import { maxBy, truncate } from 'lodash';
import moment from 'moment';
import { Component, Vue } from 'vue-property-decorator';
import feedbooksThemaMapping from './feedbooks-thema';
import { License, Publication } from './opds2';
import { thema } from './thema';
import { TranslateResult } from 'vue-i18n';

/*
 * Mixin for general utils that many components might need.
 */
@Component
export class UtilMixin extends Vue {
  private seriesColors = [
    '#d70206',
    '#f05b4f',
    '#f4c63d',
    '#d17905',
    '#453d3f',
    '#59922b',
    '#0544d3',
    '#0544d3',
    '#6b0392',
    '#f05b4f',
    '#dda458',
    '#eacf7d',
    '#86797d',
    '#b2c326',
    '#6188e2',
    '#a748ca',
  ];

  /*
   * Given a thema ID, return a human readable string.
   * TODO: i18n-ize
   */
  public formatThema(label: string): string {
    return thema[label] || 'ERROR';
  }

  /*
   * Return the color string for a series within a chartist chart, given the
   * index.
   */
  public seriesColor(index: number): string {
    return this.seriesColors[index];
  }

  public seriesColorIndexForLabel(label: string): number {
    let hash = 0;

    for (let i = 0; i < label.length; i++) {
      const chr = label.charCodeAt(i);
      hash = (hash << 5) - hash + chr; // eslint-disable-line no-bitwise
      hash |= 0; // Convert to 32bit integer
    }
    return hash % this.seriesColors.length;
  }

  /*
   * Return the color string for a series within a chartist chart, given the
   * index.
   */
  public seriesColorForLabel(label: string): string {
    return this.seriesColor(this.seriesColorIndexForLabel(label));
  }

  public aYearAgoBegginingOfDayISOString(): string {
    return moment().startOf('day').subtract(1, 'years').toISOString();
  }

  public begginingOfDayISOString(): string {
    return moment().startOf('day').toISOString();
  }

  public findThumbnailUrl(publication: Publication): string {
    let image = publication.images[0];
    for (const candidate of publication.images) {
      if (candidate.width * candidate.height < image.width * candidate.height) {
        image = candidate;
      }
    }
    return image.href;
  }

  public findSelfUrl(publication: Publication): string {
    const selfLink = publication.links.find((link) => link.rel === 'self');
    if (selfLink == null) {
      throw new Error('no self link found');
    }
    return selfLink.href;
  }

  public findDefaultLicense(publication: Publication): License {
    let licenses = publication.licenses;
    const epubLicenses = publication.licenses.filter(
      (l) => l.metadata.format === 'application/epub+zip'
    );
    if (epubLicenses.length > 0) {
      licenses = epubLicenses;
    }
    return maxBy<License>(
      licenses,
      (l) => l.metadata.terms.concurrency
    ) as License;
  }

  public formatMonthYear(year: number, month: number): string {
    return moment([year, month - 1]).format('MMM/YY');
  }

  public truncate(value: string, length: number): string {
    return truncate(value, { length, separator: ' ' });
  }

  public formatProjectGoal(value: string): TranslateResult {
    switch (value) {
      case 'PARTICULAR_CATEGORY':
        return this.$t('common.project.goal.particular_category');
      case 'IMPROVE_CATALOG':
        return this.$t('common.project.goal.improve_catalog');
      case 'NEW_RELEASES':
        return this.$t('common.project.goal.new_releases');
    }
    throw new Error('undefined project goal type');
  }

  public formatFeedbooksSubject(subject: string): string | undefined {
    const themaString = feedbooksThemaMapping[subject];
    if (themaString == null) {
      return undefined;
    } else {
      return this.$t(`thema.${themaString.substring(0, 2)}`).toString();
    }
  }

  public formatProjectState(value: string): TranslateResult {
    switch (value) {
      case 'CREATED':
        return this.$t('common.project.state.created');
      case 'CLOSED':
        return this.$t('common.project.state.closed');
      case 'CANCELED':
        return this.$t('common.project.state.canceled');
      case 'IN_PROGRESS':
        return this.$t('common.project.state.in_progress');
    }
    throw new Error('undefined project state type');
  }

  public isSuggestionEntryMissingPopular(suggestionEntry: any): boolean {
    return suggestionEntry.source.type === 'MISSING_POPULAR';
  }

  public isSuggestionAwards(suggestionEntry: any): boolean {
    return suggestionEntry.source.type === 'AWARDS';
  }

  public isSuggestionStaffPicks(suggestionEntry: any): boolean {
    return suggestionEntry.source.type === 'STAFF_PICKS';
  }

  public isSuggestionEntryKeyword(suggestionEntry: any): boolean {
    return suggestionEntry.source.type === 'KEYWORD';
  }

  public isSuggestionEntrySearch(suggestionEntry: any): boolean {
    return suggestionEntry.source.type === 'SEARCH';
  }

  public isSuggestionEntryHighDemand(suggestionEntry: any): boolean {
    return suggestionEntry.source.type === 'HIGH_DEMAND';
  }

  public isSuggestionEntryCollaborativeFiltering(
    suggestionEntry: any
  ): boolean {
    return suggestionEntry.source.type === 'COLLABORATIVE_FILTERING';
  }
}
